import React, { useEffect, useMemo, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import moment from 'moment';
import { Segment, TableColumn } from 'tea-component';
import { formatExeclNormalPercent, formatExeclNum, formatNum, formatPercentNum, getChartDateFormatString, handleDownload } from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import { getFunnelTends } from '@/pages/dashboard/services/funnel';
import useStepOptions from "@/pages/dashboard/pages/funnel/_config/useStepOptions";

import './index.scss';
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from '@/pages/home/_config/config';
import { formatFunnelFilterValues } from '../../_config/utils';

function RevenueChannelDistribution(props: any) {
  const { formValue, gameCode, filterLoading, pageType } = props;
  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl();
  const stepOptions = useStepOptions(formValue);
  const timeType = 'Day';
  const title = formatMessage({ id: 'funnel_trends' });
  const segmentOptions: any = [
    { text: formatMessage({ id: "funnel_conversion" }), value: 'conversion' },
    { text: formatMessage({ id: "funnel_count" }), value: 'count' },
  ];
  const [trendsData, setTrendsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [segValue, setSegValue] = useState('conversion');
  const stepValue = formValue?.filterValue?.stage || [];

  useEffect(() => {
    if (filterLoading) return;
    getTrendsData();
  }, [formValue, filterLoading]);

  const getRequestParams = (formValue: any) => {
    const { filterValue, newFilter, diff } = formValue || {};
    const { rangeTime, ...filters } = filterValue || {};
    let startTime = filterValue?.rangeTime?.[0];
    const endTime = filterValue?.rangeTime?.[1];

    if (newFilter && diff) {
      switch (timeType) {
        case "Day": {
          startTime = moment().subtract(7, 'day').format('YYYY-MM-DD');
          break;
        }
      }
    }

    return {
      game_code: gameCode,
      period: 'daily',
      start_time: moment(startTime).format("YYYY-MM-DD"),
      end_time: moment(endTime).format("YYYY-MM-DD"),
      filters: formatFunnelFilterValues(filters),
    };
  };

  const getTrendsData = () => {
    setError(false);
    setLoading(true);
    const params = getRequestParams(formValue);
    getFunnelTends(params, pageType).then((res) => {
      setLoading(false);
      setTrendsData(res);
    }).catch(() => {
      setLoading(false);
      setError(true);
    });
  };

  const options = useMemo(() => {
    // 表头固定列
    const dateColumn: TableColumn[] = [
      {
        key: 'date',
        align: 'left',
        fixed: 'left',
        width: 160,
        header: formatMessage({ id: 'common_date' }),
        render: (cvm: any) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
            {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      },
    ];
    const stepColumn: TableColumn[] = stepValue?.map((item: any) => {
      return {
        key: item,
        align: 'right',
        width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        header: stepOptions?.find((v: any) => v?.value === item)?.text || item,
      };
    });
    const tableOption = [...dateColumn, ...stepColumn];
    const tableData: any = [];
    const downloadData: any = [];
    // 表数据
    trendsData?.map((v: any) => {
      const obj: any = { date: v?.date };
      const downObj: any = { date: v?.date };
      stepValue?.map((item: any) => {
        const metric = v?.metrics?.[`${item  }_${  segValue}`];
        const metricValue = segValue === 'conversion' ? formatPercentNum(metric?.value) : formatNum(parseFloat(metric?.value), false, true);
        obj[item] = metricValue ?? '-';
        downObj[item] = segValue === 'conversion' ? formatExeclNormalPercent(metric?.value ?? '-') : formatExeclNum(metric?.value ?? '-');
      });
      tableData.push(obj);
      downloadData.push(downObj);
    });

    return {
      table: {
        option: tableOption,
        data: tableData,
        downloadData
      },
    };
  }, [trendsData, stepValue, segValue, getLocale()]);

  const legendData = stepValue?.map((item: any) => {
    return stepOptions?.find((v: any) => v?.value === item)?.text || item;
  });
  const xAxisData = trendsData?.map((item: any) => item?.date);
  const lineSeries = useMemo(() => {
    return stepValue?.map((item: any, index: any) => {
      const data: any = [];
      trendsData?.map((v: any) => {
        const metric = v?.metrics?.[`${item  }_${  segValue}`];
        data?.push(metric?.value ?? '');
      });
      return {
        name: legendData?.[index] || item,
        data,
        type: 'line',
        symbol: 'circle',
      };
    });
  }, [trendsData, segValue, getLocale()]);

  const barSeries = useMemo(() => {
    return stepValue?.map((item: any, index: any) => {
      const data: any = [];
      trendsData?.map((v: any) => {
        const metric = v?.metrics?.[`${item  }_${  segValue}`];
        data?.push(metric?.value ?? '');
      });
      const seriesItem: any = {
        name: legendData?.[index] || item,
        data,
        type: 'bar',
        symbol: 'circle',
        barGap: '5%',
        barCategoryGap: '50%',
      };
      if (data?.length > 7) seriesItem.stack = 'bar';
      return seriesItem;
    });
  }, [trendsData, segValue, getLocale()]);
  const isNodata = lineSeries?.filter((item: any) => item?.data?.length)?.length === 0;

  const formatterFunc = (params: any, timeType = 'Day', segValue: any) => {
    const weekDay = getWeekDay(params[0].axisValue);
    return getEchartTooltipText({
      title: `${moment(`${params[0].axisValue}`).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD')  }(${  weekDay  })`,
      metricsName: formatMessage({ id: `funnel_${segValue}` }),
      data: [...params]
        // .sort((f, s) => s.value - f.value)
        .map(({ seriesName, value, color }) => ({
          name: seriesName,
          metricsNum: segValue === 'conversion' ? formatPercentNum(value) : formatNum(parseFloat(value), false, true),
          color,
        })),
      // isRemoveNull: true,
      isSort: true
    });
  };

  const renderList = [{
    type: 'barChart',
    svgType: 'column-chart',
    content: formatMessage({ id: 'common_bar_chart' }),
    component: <BarChart
      height={390}
      legendData={xAxisData.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData.length > 0 ? barSeries : []}
      formatterFunc={(params: any) => formatterFunc(params, timeType, segValue)}
      setOtherConfigFunc={(opt: any) => {
        opt.xAxis[0].axisLabel.formatter = (value) => getChartDateFormatString(timeType, value);
        opt.yAxis.axisLabel.formatter = (value: any) => {
          const metricValue = segValue === 'conversion' ? formatPercentNum(value) : formatNum(parseFloat(value), false, true);
          return metricValue;
        };
        opt = xAxisHighLight(opt, {
          formatter: (params: { value: string; }) => {
            return getChartDateFormatString(timeType, params.value);
          }
        });
        return opt;
      }}
    />
  },
  {
    type: 'lineChart',
    svgType: 'discount-chart',
    content: formatMessage({ id: 'common_line_chart' }),
    component: <LineChart
      height={390}
      legendData={xAxisData.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData.length > 0 ? lineSeries : []}
      formatterFunc={(params: any) => formatterFunc(params, timeType, segValue)}
      setOtherConfigFunc={(opt: any) => {
        opt.xAxis[0].axisLabel.formatter = (value) => getChartDateFormatString(timeType, value);
        opt.yAxis.axisLabel.formatter = (value: any) => {
          const metricValue = segValue === 'conversion' ? formatPercentNum(value) : formatNum(parseFloat(value), false, true);
          return metricValue;
        };
        opt = xAxisHighLight(opt, {
          formatter: (params: { value: string; }) => {
            return getChartDateFormatString(timeType, params.value);
          }
        });
        return opt;
      }}
    />
  },
  {
    type: 'sheet',
    svgType: 'sheet',
    content: formatMessage({ id: 'common_table' }),
    component: <MiniTable
      height={390}
      loading={false}
      tableOption={options?.table?.option}
      tableList={options?.table?.data}
    />
  }];

  return <CardBox
    height={390}
    bordered={false}
    titleCom={title}
    filterSlot={
      <div className='dbd-trends-flex'>
        <Segment
          className="step-flex-segment"
          rimless={false}
          value={segValue}
          onChange={(value) => setSegValue(value)}
          options={segmentOptions}
        />
      </div>
    }
    renderList={renderList || []}
    defaultAvtive={1}
    downloadClick={async () => {
      handleDownload(options?.table, title);
    }}
    isLoading={loading}
    isError={error}
    isNodata={isNodata}
  />;
}

export default RevenueChannelDistribution;
